// src/components/BillSelectionDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Typography,
  Box,
  Divider,
  IconButton,
  useTheme,
  alpha
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Close as CloseIcon,
//   Warning as WarningIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

const BillSelectionDialog = ({ open, onClose, pendingTransactions, onSelectBill, formatCurrency }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isDark
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Select Bill for Payment
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {pendingTransactions.length} bill(s) have pending payments
        </Typography>
      </DialogTitle>

      <DialogContent>
        <List sx={{ pt: 0 }}>
          {pendingTransactions.map((transaction, index) => {
            const pendingAmount = transaction.summary?.pendingAmount || 
                                 transaction.transactionDetails?.balanceDue || 0;
            const billNo = transaction.transactionDetails?.billNo || 'N/A';
            const date = transaction.date || 'Unknown Date';
            
            return (
              <React.Fragment key={transaction.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      transform: 'translateX(4px)',
                    }
                  }}
                  onClick={() => onSelectBill(transaction)}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight={600}>
                          Bill #{billNo}
                        </Typography>
                        <Chip
                          label={pendingAmount > 0 ? 'Pending' : 'Partial'}
                          size="small"
                          sx={{
                            bgcolor: pendingAmount > 0 ? 'warning.main' : 'info.main',
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 22
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          Date: {date}
                        </Typography>
                        <Box display="flex" gap={2} mt={0.5}>
                          <Typography variant="body2">
                            Total: <strong>{formatCurrency(transaction.summary?.grandTotal || 0)}</strong>
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            Paid: {formatCurrency(transaction.summary?.paymentDone || 0)}
                          </Typography>
                          <Typography variant="body2" color="error.main">
                            Due: {formatCurrency(pendingAmount)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PaymentIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectBill(transaction);
                      }}
                      sx={{
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        }
                      }}
                    >
                      Pay Now
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} fullWidth variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillSelectionDialog;